import React, { createContext, useContext, useState, useEffect } from 'react';
import { saveStateToStorage, getStateFromStorage, removeStateFromStorage } from '~/util/storageUtils';

// Define the context state type
interface AppBuilderState {
    appIdea: string | null;
    isSubmitting: boolean;
    setAppIdea: (idea: string) => void;
    clearAppIdea: () => void;
    startSubmission: () => void;
    endSubmission: () => void;
}

// Storage key for app builder state
const APP_BUILDER_STATE_KEY = 'appBuilderContextState';

// Create the context with default values
export const AppBuilderContext = createContext<AppBuilderState>({
    appIdea: null,
    isSubmitting: false,
    setAppIdea: () => { },
    clearAppIdea: () => { },
    startSubmission: () => { },
    endSubmission: () => { },
});

// Define provider props
interface AppBuilderProviderProps {
    children: React.ReactNode;
}

// Create the provider component
export const AppBuilderProvider: React.FC<AppBuilderProviderProps> = ({ children }) => {
    // Initialize state from localStorage if available
    const initialState = (() => {
        if (typeof window === 'undefined') return null;

        try {
            const savedState = getStateFromStorage<{ appIdea: string, timestamp: number }>(APP_BUILDER_STATE_KEY);
            if (savedState && savedState.appIdea) {

                return savedState.appIdea;
            }
        } catch (err) {
            console.error('[AppBuilderContext] Error loading state from storage:', err);
        }
        return null;
    })();

    const [appIdea, setAppIdeaState] = useState<string | null>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Log the current state for debugging
    useEffect(() => {
        console.log('[AppBuilderContext] State updated:', {
            hasAppIdea: !!appIdea,
            appIdeaLength: appIdea?.length || 0,
            isSubmitting
        });
    }, [appIdea, isSubmitting]);

    // Persist appIdea to localStorage whenever it changes
    useEffect(() => {
        if (typeof window === 'undefined') return;

        if (appIdea) {

            try {
                saveStateToStorage(APP_BUILDER_STATE_KEY, {
                    appIdea,
                    timestamp: Date.now()
                });
            } catch (err) {
                console.error('[AppBuilderContext] Error saving state to storage:', err);
            }
        }
    }, [appIdea]);

    // Method to set the app idea
    const setAppIdea = (idea: string) => {

        setAppIdeaState(idea);
    };

    // Method to clear the app idea
    const clearAppIdea = () => {

        setAppIdeaState(null);

        // Also clear from storage
        if (typeof window !== 'undefined') {
            try {
                removeStateFromStorage(APP_BUILDER_STATE_KEY);
            } catch (err) {
                console.error('[AppBuilderContext] Error clearing state from storage:', err);
            }
        }
    };

    // Methods to track submission state
    const startSubmission = () => {

        setIsSubmitting(true);
    };

    const endSubmission = () => {

        setIsSubmitting(false);
    };

    // Provide the context value
    const value: AppBuilderState = {
        appIdea,
        isSubmitting,
        setAppIdea,
        clearAppIdea,
        startSubmission,
        endSubmission,
    };

    return (
        <AppBuilderContext.Provider value={value}>
            {children}
        </AppBuilderContext.Provider>
    );
};

// Custom hook to use the app builder context
export const useAppBuilder = () => {
    const context = useContext(AppBuilderContext);

    if (!context) {
        throw new Error('useAppBuilder must be used within an AppBuilderProvider');
    }

    return context;
}; 